import Markdown, { defaultUrlTransform } from "react-markdown";
import classNames from "classnames";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeRaw from "rehype-raw";
import { CodeBlock } from "./CodeBlock";
import { MessageFileTag } from "./MessageFileTag";
import { memo, useMemo, useRef } from "react";
import { wrapUnprotectedHtmlTags } from "./utils/wrapUnprotectedHtmlTags/wrapUnprotectedHtmlTags";
import { ImageTag } from "./ImageTag";
import styles from "./styles.module.css";
import { extractFilename } from "./utils/extractFilename";

interface Props {
  content: string;
  onFileClick?: (url: string, filename: string) => void;
  className?: string;
}

export const AppMarkdownParserInner = ({
  content,
  onFileClick,
  className,
}: Props) => {
  const fileDataMap = useRef<Map<string, { url: string; filename: string }>>(
    new Map(),
  );

  const processedContent = useMemo(() => {
    fileDataMap.current.clear();
    const safeContent = typeof content === "string" ? content : "";
    return safeContent?.replace(
      /<file[^>]*data-url="([^"]*)"[^>]*data-filename="([^"]*)"[^>]*>([\s\S]*?)<\/file>/gi,
      (_, url, filename, text) => {
        const id = Math.random().toString(36).slice(2);
        fileDataMap.current.set(id, { url, filename });
        return `<file id="${id}">${text}</file>`;
      },
    );
  }, [content]);

  const customComponents: Partial<Record<string, any>> = {
    code: CodeBlock,
    img: (props: any) => <ImageTag src={props.src} />,
    file: (props: any) => {
      const id = props.id?.replace("user-content-", "");
      const data = fileDataMap.current.get(id);
      return (
        <MessageFileTag
          onFileClick={onFileClick}
          dataUrl={data?.url}
          dataFilename={data?.filename}
        >
          {props.children}
        </MessageFileTag>
      );
    },
    a: (props: any) => {
      const { href, children } = props;

      if (href?.startsWith("data:") || href?.startsWith("blob:")) {
        const filename = extractFilename(href, children);
        return (
          <a href={href} download={filename}>
            {children}
          </a>
        );
      }

      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
  };

  const mySchema = {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames || []), "file"],
    attributes: {
      ...defaultSchema.attributes,
      file: ["id"],
      "*": [...(defaultSchema.attributes?.["*"] || []), "id"],
    },
    protocols: {
      ...(defaultSchema.protocols || {}),
      href: [...(defaultSchema.protocols?.href || []), "blob", "data"],
    },
  };

  const customUrlTransform = (url: string) => {
    if (url.startsWith("data:")) {
      const isSafe = /^data:(text\/plain|text\/csv|application\/json);/i.test(
        url,
      );
      return isSafe ? url : "";
    }
    return defaultUrlTransform(url);
  };

  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, mySchema]]}
      components={customComponents}
      urlTransform={customUrlTransform}
      className={classNames(styles.markdown, className)}
    >
      {wrapUnprotectedHtmlTags(processedContent)}
    </Markdown>
  );
};

export const AppMarkdownParser = memo(AppMarkdownParserInner);
